import React, { useContext, useEffect, useState } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  useColorScheme,
  View,
} from "react-native";
import { AntDesign, Feather } from "@expo/vector-icons";
import { useIsFocused } from "@react-navigation/native";
import { useToast } from "react-native-toast-notifications";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import UpdateConfirmationScreen from "../../Operations/partials/UpdateConfirmationScreen";
import {
  deleteBypassUserById,
  getBypassUserDetailsById,
} from "../../../services/usersService";
import TabBarIcon from "../../../components/core/TabBarIcon";
import CompanyLogo from "../../../components/core/CompanyLogo";
import oktaConfig from "../../../configs/okta";
import ContentTitleHeader from "../../../components/core/ContentTitleHeader";
import { getItemFromSessionStorage } from "../../../services/utils/storageService";
import { useAppTheme } from "../../../hooks/useAppTheme";
import type { ThemeColors } from "../../../utils/constants/colors";

interface Props {
  navigation: any;
  route: any;
}

const CreateOktaUserNavigate: React.FC<Props> = ({ navigation, route }) => {
  const { colors, isDark } = useAppTheme();
  const styles = createStyles(colors, isDark);
  const colorScheme = useColorScheme();
  const toast = useToast();
  const { isMediumDevice, isMobileDevice, isSmallDevice } = useContext(
    DeviceOrientationContext
  );
  const isFocused = useIsFocused();
  const [title, setTitle] = useState("");
  const [action, setAction] = useState("");
  const [confirmationTextCS, setConfirmationTextCS] = useState("");
  const [successfulTextCS, setSuccessfulTextCS] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [approved, setApproved] = useState(false);
  let { mode, userId } = route.params;
  const [selectedUser, setSelectedUser] = useState(mode || null);

  useEffect(() => {
    if (mode == undefined || userId == undefined) {
      setTimeout(() => {
        navigation.reset({
          index: 0,
          routes: [{ name: "OrgSettings", params: { screen: "Users" } }],
        });
      }, 1500);
    } else {
      getBypassUserDetailsById(userId)
        .then((data) => setSelectedUser(data))
        .catch((error) => toast.show(error));
    }
  }, [mode]);

  useEffect(() => {
    if (isFocused) {
      switch (mode) {
        case "edit":
          setTitle("View User");
          setApproved(true);
          break;
        case "delete":
          setTitle("Delete User");
          setApproved(false);
          break;
        default:
          break;
      }
      setAction(mode);
      setLoaded(true);
    } else setLoaded(false);
  }, [isFocused]);

  useEffect(() => {
    // Handle title and confirmation text based on action
    switch (action) {
      case "edit":
        setTitle("View User");
        break;
      case "delete":
        setTitle("Delete User");
        setConfirmationTextCS(
          "Are you sure you want to delete the bypass user?"
        );
        break;
      default:
        setTitle("");
        setConfirmationTextCS("");
        break;
    }
  }, [action]);

  useEffect(() => {
    // Directly call deleteOperation when approved and action is delete
    if (approved && action === "delete") {
      // Validate selectedUser before attempting to delete
      if (selectedUser && selectedUser._id) {
        deleteDirectOperation(selectedUser._id);
      } else {
        console.error("No user selected for deletion.");
      }
    }
  }, [approved, action]);

  const approveCS = () => {
    setApproved(true);
  };

  const rejectCS = () => {
    navigation.reset({
      index: 0,
      routes: [{ name: "OrgSettings", params: { screen: "Users" } }],
    });
  };

  const updateOperation = () => {
    navigation.navigate("UpdateUser", { userId: userId });
  };

  const mobileDevice = isSmallDevice || isMediumDevice || isMobileDevice;

  const deleteOperation = async (id: string) => {
    if (confirm("Do you want to Delete this Operation?")) {
      await deleteBypassUserById(id)
        .then((result) => {
          toast.show(result?.data.message, {
            type: "success",
          });
          navigation.reset({
            index: 0,
            routes: [{ name: "OrgSettings", params: { screen: "Users" } }],
          });
        })
        .catch((err) => {
          toast.show(err.message, {
            type: "danger",
          });
        });
    }
  };

  const deleteDirectOperation = async (id: string) => {
    await deleteBypassUserById(id)
      .then((result) => {
        toast.show(result?.data.message, {
          type: "success",
        });
        navigation.reset({
          index: 0,
          routes: [{ name: "OrgSettings", params: { screen: "Users" } }],
        });
        // backToList();
      })
      .catch((err) => {
        toast.show(err.message, {
          type: "danger",
        });
      })
      .finally(() => {
        // setDisplayLoader(!displayLoader);
      });
  };

  const updateCurrentAction = (action: string) => {
    setAction((prev) => action);
  };

  const DrawerHeader = () => {
    return (
      <View style={styles.drawerStyle}>
        <View style={styles.drawerLeft}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <AntDesign name="arrowleft" size={20} color={colors.text} />
          </TouchableOpacity>
          <View style={styles.titleContainer}>
            <Text style={styles.drawerTitle}>{title}</Text>
            {selectedUser?.display_name && (
              <Text style={styles.drawerSubtitle}>
                {selectedUser.display_name}
              </Text>
            )}
          </View>
        </View>
        <View style={styles.drawerRight}>
          <TouchableOpacity
            style={styles.deleteButton}
            onPress={() => deleteOperation(selectedUser._id)}
          >
            <Feather name="trash-2" size={18} color={colors.danger} />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const DetailRow = ({ label, value: val, isLast = false }: { label: string; value: string; isLast?: boolean }) => (
    <View style={[styles.row, !isLast && styles.rowBorder]}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{val || "—"}</Text>
    </View>
  );

  const RoleBadge = ({ role }: { role: string }) => (
    <View style={styles.roleBadge}>
      <Text style={styles.roleBadgeText}>{role?.toUpperCase()}</Text>
    </View>
  );

  return (
    <>
      {approved ? (
        <ScrollView
          style={styles.pageContainer}
        >
          {!isSmallDevice && (
            <View style={{ flexDirection: "row", width: "100%", height: 60 }}>
              <View
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  width: "40%",
                  height: 60,
                }}
              >
                <CompanyLogo />
              </View>
              <View
                style={{
                  justifyContent: "center",
                  alignItems: "center",
                  width: "60%",
                  height: 60,
                }}
              >
                <TouchableOpacity
                  onPress={() => navigation.navigate("Root")}
                >
                  <TabBarIcon name="home" color={colors.text} />
                </TouchableOpacity>
              </View>
            </View>
          )}
          <DrawerHeader />

          <View style={styles.contentArea}>
            {/* User Details Section */}
            <View style={styles.card}>
              <View style={styles.cardAccent} />
              <View style={styles.cardContent}>
                <View style={styles.cardHeader}>
                  <View style={styles.cardTitleRow}>
                    <Feather name="user" size={18} color="#3A9FE8" />
                    <Text style={styles.cardTitle}>User Details</Text>
                  </View>
                  {action === "edit" && (
                    <TouchableOpacity
                      style={styles.editButton}
                      onPress={updateOperation}
                    >
                      <Feather name="edit-2" size={15} color="#3A9FE8" />
                      <Text style={styles.editButtonText}>Edit</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <DetailRow label="First Name" value={selectedUser.first_name} />
                <DetailRow label="Last Name" value={selectedUser.last_name} />
                <DetailRow label="Username" value={selectedUser.login || ""} />
                <DetailRow label="Zone" value={selectedUser.zone_info} />
                <DetailRow label="Email" value={selectedUser.email} isLast />
              </View>
            </View>

            {/* Summary Section */}
            <View style={styles.card}>
              <View style={[styles.cardAccent, { backgroundColor: "#8ECBFF" }]} />
              <View style={styles.cardContent}>
                <View style={styles.cardHeader}>
                  <View style={styles.cardTitleRow}>
                    <Feather name="briefcase" size={18} color="#3A9FE8" />
                    <Text style={styles.cardTitle}>Summary</Text>
                  </View>
                  {action === "edit" && (
                    <TouchableOpacity
                      style={styles.editButton}
                      onPress={updateOperation}
                    >
                      <Feather name="edit-2" size={15} color="#3A9FE8" />
                      <Text style={styles.editButtonText}>Edit</Text>
                    </TouchableOpacity>
                  )}
                </View>
                <View style={[styles.row, styles.rowBorder]}>
                  <Text style={styles.label}>Team</Text>
                  <Text style={styles.value}>
                    {selectedUser.team
                      ? selectedUser.team.replace(/_/g, " ")
                      : "—"}
                  </Text>
                </View>
                <View style={[styles.row, styles.rowBorder]}>
                  <Text style={styles.label}>Role</Text>
                  {selectedUser?.roles?.length > 0 &&
                    selectedUser?.roles?.[0]?.role ? (
                    <RoleBadge role={selectedUser.roles[0].role} />
                  ) : (
                    <Text style={styles.value}>—</Text>
                  )}
                </View>
                <View style={styles.row}>
                  <Text style={styles.label}>Company</Text>
                  <Text style={styles.value}>
                    {(() => {
                      try {
                        const companyContext = JSON.parse(
                          getItemFromSessionStorage("company-context") as string
                        );
                        return companyContext ? companyContext.company_name : "—";
                      } catch (e) {
                        return "—";
                      }
                    })()}
                  </Text>
                </View>
              </View>
            </View>
          </View>
        </ScrollView>
      ) : (
        <UpdateConfirmationScreen
          confirmationText={confirmationTextCS}
          successfulText={successfulTextCS}
          approve={approveCS}
          reject={rejectCS}
        />
      )}
    </>
  );
};

function createStyles(colors: ThemeColors, isDark: boolean) {
  return StyleSheet.create({
    pageContainer: {
      height: "100%",
      backgroundColor: colors.background,
    },
    contentArea: {
    paddingHorizontal: 24,
    paddingVertical: 20,
    maxWidth: 800,
    width: "100%",
    alignSelf: "center",
  },
  content: {
    paddingHorizontal: 15,
    paddingBottom: 25,
  },
  formHeaders: {
    color: "#B5B5B5",
    fontSize: 20,
    fontWeight: "500",
    marginBottom: 7,
  },
  formSection: {
    paddingVertical: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#000",
  },
  customerRow: {
    flexDirection: "row",
    paddingVertical: 8,
  },
  shippingRow: {
    flexDirection: "row",
    paddingVertical: 5,
  },
  checkbox: {
    paddingLeft: 0,
    marginLeft: -7,
    paddingVertical: 0,
  },
  checkboxLabel: {
    marginTop: 2,
    textAlign: "left",
    justifyContent: "center",
    fontWeight: "500",
  },
  datatableHeader: {
    height: 45,
    backgroundColor: "#D9D9D9",
  },
  productColumn: {
    flex: 2.75,
    paddingVertical: 0,
    justifyContent: "flex-start",
    alignItems: "flex-start",
  },
  orderQtyColumn: {
    flex: 0.75,
    paddingVertical: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  uom: {
    flex: 1.1,
    justifyContent: "center",
    paddingVertical: 0,
    alignItems: "center",
  },
  inventory: {
    flex: 1.1,
    justifyContent: "center",
    paddingVertical: 0,
    alignItems: "center",
  },
  price: {
    flex: 1.2,
    justifyContent: "flex-end",
    paddingVertical: 0,
    alignItems: "center",
  },
  remarksInput: {
    borderWidth: 1,
    borderColor: "#000",
    height: 60,
    padding: 4,
  },
  datatableHeaderItemText: {
    fontSize: 12,
    textAlign: "center",
    lineHeight: 16,
    color: "#000",
  },
  headerBtnContainer: {
    padding: 5,
    marginHorizontal: 3,
  },
  fileList: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-evenly",
    marginTop: 20,
  },
  filePreviewHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingHorizontal: 8,
    paddingVertical: 5,
    backgroundColor: "#f8f8f8",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
  },
  fileNameText: {
    fontSize: 12,
    fontWeight: "400",
    color: "#333",
    flexWrap: "wrap",
    maxWidth: "80%",
  },
  fileLink: {
    fontSize: 12,
    color: "#007bff",
    textDecorationLine: "underline",
    textAlign: "center",
    marginTop: 5,
  },
  previewContainer: {
    justifyContent: "center",
    alignItems: "center",
  },
  imagePreview: {
    width: "100%",
    height: 80,
    backgroundColor: "#f5f5f5",
    borderTopWidth: 1,
    borderTopColor: "#eee",
  },
  cardImage: {
    width: "100%",
    height: "100%",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
  },
  placeholderIconContainer: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#e0e0e0",
    width: "100%",
    height: 80,
  },
  pdfLink: {
    fontSize: 12,
    color: "#007aff",
    textDecorationLine: "underline",
    textAlign: "center",
    marginVertical: 3,
  },

    // Modern drawer header
    drawerStyle: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      borderBottomWidth: 1,
      borderColor: colors.borderSubtle,
      position: "sticky" as any,
      top: 0,
      width: "100%",
      zIndex: 999,
      backgroundColor: colors.surface,
      height: 56,
      paddingHorizontal: 16,
    },
    drawerLeft: {
      flexDirection: "row",
      alignItems: "center",
      flex: 1,
    },
    backButton: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: colors.surfaceSubtle,
      justifyContent: "center",
      alignItems: "center",
    },
    titleContainer: {
      marginLeft: 12,
    },
    drawerTitle: {
      fontSize: 16,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: 0.2,
    },
    drawerSubtitle: {
      fontSize: 13,
      color: colors.textSecondary,
      marginTop: 1,
    },
    drawerRight: {
      flexDirection: "row",
      alignItems: "center",
    },
    deleteButton: {
      width: 36,
      height: 36,
      borderRadius: 8,
      backgroundColor: colors.dangerBg,
      justifyContent: "center",
      alignItems: "center",
    },

  container: {
    flex: 1,
    backgroundColor: "#f8fafc",
    padding: 16,
  },
  header: {
    marginBottom: 16,
    padding: 16,
    backgroundColor: "#007BFF",
    borderRadius: 8,
    shadowColor: "#000",
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  headerText: {
    color: "#FFF",
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
  },

    // Modern card styles
    card: {
      backgroundColor: colors.surface,
      borderRadius: 12,
      marginBottom: 20,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.06,
      shadowRadius: 12,
      elevation: 3,
      flexDirection: "row",
      overflow: "hidden",
      borderWidth: 1,
      borderColor: colors.borderSubtle,
    },
    cardAccent: {
      width: 4,
      backgroundColor: colors.primary,
    },
  cardContent: {
    flex: 1,
    padding: 20,
  },
    cardHeader: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: 16,
      paddingBottom: 12,
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
    },
    cardTitleRow: {
      flexDirection: "row",
      alignItems: "center",
    },
    cardTitle: {
      fontSize: 17,
      fontWeight: "700",
      color: colors.text,
      marginLeft: 8,
      letterSpacing: 0.2,
    },
    editButton: {
      flexDirection: "row",
      alignItems: "center",
      paddingVertical: 6,
      paddingHorizontal: 12,
      borderRadius: 6,
      backgroundColor: colors.surfaceSubtle,
    },
    editButtonText: {
      fontSize: 13,
      fontWeight: "600",
      color: colors.primary,
      marginLeft: 5,
    },
    row: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      paddingVertical: 10,
    },
    rowBorder: {
      borderBottomWidth: 1,
      borderBottomColor: colors.borderSubtle,
    },
    label: {
      fontSize: 14,
      color: colors.textSecondary,
      fontWeight: "500",
    },
    value: {
      fontSize: 14,
      color: colors.text,
      fontWeight: "600",
    },
    valueMono: {
      fontSize: 14,
      color: colors.text,
      fontWeight: "600",
      fontFamily: "monospace",
      letterSpacing: 0.5,
    },
    roleBadge: {
      backgroundColor: colors.surfaceSubtle,
      paddingVertical: 3,
      paddingHorizontal: 10,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    roleBadgeText: {
      fontSize: 12,
      fontWeight: "700",
      color: colors.text,
      letterSpacing: 0.5,
    },
  });
}

export default CreateOktaUserNavigate;
